import { memo, useCallback, useLayoutEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { FieldsProvider, useFieldsReadiness } from './Fields.Context';
import type { FieldsProviderProps } from './Fields.Context';
import { EditorWrapper } from '../Editor';
import { FieldsSectionEditor } from './Fields.SectionEditor';
import { FieldsSubmit } from './Fields.Submit';
import { FieldsAddendumEditor } from './Fields.AddendumEditor';
import { FieldsAddendumRenderer } from './Fields.AddendumRenderer';
import { ReportStatuses, reportStatusState, SECTION_EDITOR_EDITABLE_STATUSES } from '../state';
import { FieldsSectionRenderer } from './Fields.SectionRenderer';
import { useCurrentCaseReport } from 'hooks/useCurrentCaseReport';
import { Stack } from 'common/ui/Layout';
import { FieldsSubmitToggleRow } from './Fields.SubmitToggleRow';
import type { ReporterEditorSurface, ReporterPrimarySurface } from '../ReporterLoadReadiness.types';
import { isAddendumEditorEditable } from './utils';

export type FieldsProps = FieldsProviderProps & {
  isLoading?: boolean;
  shouldRenderSectionEditor: boolean;
  onEditorInitialized?: (surface: ReporterEditorSurface) => void;
  onReadyStateChange?: (state: 'editable' | 'read_only' | null) => void;
  children?: React.ReactNode | undefined;
};

export const shouldRenderReporterSectionEditor = ({
  status,
  hasSubmittingCaseInGroup,
}: {
  status: ReportStatuses;
  hasSubmittingCaseInGroup: boolean;
}): boolean =>
  (SECTION_EDITOR_EDITABLE_STATUSES as ReportStatuses[]).includes(status) &&
  !hasSubmittingCaseInGroup;

const FieldsContent = ({
  isLoading,
  shouldRenderSectionEditor,
  onEditorInitialized,
  onReadyStateChange,
  ...props
}: FieldsProps): React.ReactElement => {
  const { currentCaseReport } = useCurrentCaseReport();
  const { initialStateReconciled, hasAddendum } = useFieldsReadiness();
  const { onDestroy, onInit } = props;
  const status = useRecoilValue(reportStatusState);
  const [readySurfaces, setReadySurfaces] = useState<Record<ReporterPrimarySurface, boolean>>({
    report_editor: false,
    addendum_editor: false,
    rendered_content: false,
  });

  const isSectionEditorVisible = shouldRenderSectionEditor && !props.isDisabled;
  const isCurrentAddendumEditorVisible = isAddendumEditorEditable({
    hasAddendum,
    isClaimedByMe: props.isClaimedByMe,
    isDisabled: props.isDisabled,
    status,
  });
  const primarySurface: ReporterPrimarySurface = isSectionEditorVisible
    ? 'report_editor'
    : isCurrentAddendumEditorVisible
      ? 'addendum_editor'
      : 'rendered_content';

  useLayoutEffect(() => {
    const ready =
      initialStateReconciled &&
      readySurfaces[primarySurface] &&
      (primarySurface === 'rendered_content' || !isLoading);
    if (ready && primarySurface !== 'rendered_content') onEditorInitialized?.(primarySurface);
    onReadyStateChange?.(
      ready ? (primarySurface === 'rendered_content' ? 'read_only' : 'editable') : null
    );
    return () => onReadyStateChange?.(null);
  }, [
    initialStateReconciled,
    onReadyStateChange,
    primarySurface,
    readySurfaces,
    isLoading,
    onEditorInitialized,
  ]);

  const updateSurfaceReadiness = useCallback((surface: ReporterPrimarySurface, ready: boolean) => {
    setReadySurfaces((current) =>
      current[surface] === ready ? current : { ...current, [surface]: ready }
    );
  }, []);

  const handleSectionEditorInitialized: NonNullable<FieldsProviderProps['onInit']> = useCallback(
    (details) => {
      onInit?.(details);
      onEditorInitialized?.('report_editor');
      updateSurfaceReadiness('report_editor', true);
    },
    [onEditorInitialized, onInit, updateSurfaceReadiness]
  );
  const handleSectionEditorDestroyed = useCallback(() => {
    onDestroy?.();
    updateSurfaceReadiness('report_editor', false);
  }, [onDestroy, updateSurfaceReadiness]);
  const handleAddendumEditorInitialized: NonNullable<FieldsProviderProps['onInit']> = useCallback(
    (details) => {
      onInit?.(details);
      onEditorInitialized?.('addendum_editor');
      updateSurfaceReadiness('addendum_editor', true);
    },
    [onEditorInitialized, onInit, updateSurfaceReadiness]
  );
  const handleAddendumEditorDestroyed = useCallback(
    () => updateSurfaceReadiness('addendum_editor', false),
    [updateSurfaceReadiness]
  );
  const handlePrimaryRenderedContent = useCallback(
    () => updateSurfaceReadiness('rendered_content', true),
    [updateSurfaceReadiness]
  );
  const handlePrimaryRenderedContentUnmounted = useCallback(
    () => updateSurfaceReadiness('rendered_content', false),
    [updateSurfaceReadiness]
  );

  return (
    <EditorWrapper variant="report" space="xsmall">
      <Stack
        data-stack-stretch
        space="medium"
        stretchX
        stretchY
        css="overflow: auto; height: 100%; min-height: 55%;"
      >
        {shouldRenderSectionEditor ? (
          <FieldsSectionEditor
            isDisabled={props.isDisabled}
            onInit={handleSectionEditorInitialized}
            onDestroy={handleSectionEditorDestroyed}
            onError={props.onError}
            onContentRendered={handlePrimaryRenderedContent}
            onContentUnmounted={handlePrimaryRenderedContentUnmounted}
            autoloadTemplateState={props.autoloadTemplateState}
            isLoading={isLoading}
          />
        ) : (
          <FieldsSectionRenderer
            onContentRendered={handlePrimaryRenderedContent}
            onContentUnmounted={handlePrimaryRenderedContentUnmounted}
          />
        )}
      </Stack>
      <Stack vertical space="small">
        <FieldsAddendumRenderer isClaimedByMe={props.isClaimedByMe} />
        <FieldsAddendumEditor
          isClaimedByMe={props.isClaimedByMe}
          isDisabled={props.isDisabled}
          onInit={handleAddendumEditorInitialized}
          onDestroy={handleAddendumEditorDestroyed}
          onError={props.onError}
        />
        <FieldsSubmit
          isDisabled={props.isDisabled || props.autoloadTemplateState === 'pending'}
          isClaimedByMe={props.isClaimedByMe}
          smid={currentCaseReport?.smid}
          initialExamCount={currentCaseReport?.examCount ?? 1}
        />
        <FieldsSubmitToggleRow />
      </Stack>
    </EditorWrapper>
  );
};

export const Fields = memo(function Fields({
  isLoading,
  shouldRenderSectionEditor,
  onEditorInitialized,
  onReadyStateChange,
  ...props
}: FieldsProps): React.ReactElement {
  return (
    <FieldsProvider {...props}>
      <FieldsContent
        {...props}
        isLoading={isLoading}
        shouldRenderSectionEditor={shouldRenderSectionEditor}
        onEditorInitialized={onEditorInitialized}
        onReadyStateChange={onReadyStateChange}
      />
    </FieldsProvider>
  );
});

Fields.displayName = 'Fields';
